// සුවඩි Smart Warranty Assistant - Main Component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WarrantyIcon } from './WarrantyIcon';
import { ExtractionModal } from './ExtractionModal';
import { ActivityHistoryPanel } from './ActivityHistoryPanel';
import type { SmartWarrantyAssistantProps, Activity, ExtractedData, UploadResult, StepStatus, StepData } from './types';
import { documentService } from '../../../services/document';
import { saveWarranty as saveWarrantyAPI } from '../../../services/api';
import { authService } from '../../../services/auth';
import './SmartWarrantyAssistant.css';

// Speech Recognition types - avoiding conflicts with global types
interface LocalSpeechRecognitionEvent {
  results: LocalSpeechRecognitionResultList;
}

interface LocalSpeechRecognitionResultList {
  length: number;
  [index: number]: LocalSpeechRecognitionResult;
}

interface LocalSpeechRecognitionResult {
  length: number;
  isFinal: boolean;
  [index: number]: LocalSpeechRecognitionAlternative;
}

interface LocalSpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface LocalSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: (event: LocalSpeechRecognitionEvent) => void;
  onerror: (event: any) => void;
}

// Helper function to extract warranty data from OCR text using basic pattern matching
const extractWarrantyFromOcrText = (ocrText: string, extractionResult: any): ExtractedData => {
  console.log('🔍 Manual extraction from OCR text:', ocrText.substring(0, 200) + '...');
  
  // Basic pattern matching for common warranty fields
  const patterns = {
    productName: /(?:product|item|model)[\s:]+([^\n]+)/i,
    brand: /(?:brand|make|manufacturer)[\s:]+([^\n]+)/i,
    serialNumber: /(?:serial|s\/n|sn)[\s:]+([A-Z0-9-]+)/i,
    modelNumber: /(?:model|part|item)[\s#:]+([A-Z0-9-]+)/i,
    purchaseDate: /(?:purchase|bought|order|delivery)[\s\w]*date[\s:]*([0-9]{1,2}[-\/][0-9]{1,2}[-\/][0-9]{2,4})/i,
    warrantyPeriod: /(?:warranty|guarantee)[\s\w]*(?:for|period|of)[\s:]*([0-9]+[\s-]*(?:year|month|day)s?)/i,
    retailer: /(?:purchased|bought)[\s\w]*(?:on|from|at)[\s:]*([^\n]+)/i,
    orderId: /(?:order|invoice|receipt)[\s#:]*([A-Z0-9-]+)/i
  };

  const extracted: any = {};
  
  // Extract using patterns
  for (const [field, pattern] of Object.entries(patterns)) {
    const match = ocrText.match(pattern);
    if (match && match[1]) {
      extracted[field] = match[1].trim();
    }
  }

  // Special handling for specific brands/formats
  if (ocrText.toLowerCase().includes('green soul')) {
    extracted.brand = 'Green Soul';
    extracted.category = 'Furniture';
  }

  // Calculate warranty expiry if we have purchase date and period
  let warrantyExpiryDate = '';
  if (extracted.purchaseDate && extracted.warrantyPeriod) {
    try {
      const purchaseDate = new Date(extracted.purchaseDate);
      const yearMatch = extracted.warrantyPeriod.match(/([0-9]+)\s*year/i);
      if (yearMatch) {
        const years = parseInt(yearMatch[1]);
        const expiryDate = new Date(purchaseDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + years);
        warrantyExpiryDate = expiryDate.toISOString().split('T')[0];
      }
    } catch (e) {
      console.warn('Could not calculate warranty expiry:', e);
    }
  }

  return {
    // Core Product Information
    productName: extracted.productName || '',
    brand: extracted.brand || '',
    modelNumber: extracted.modelNumber || '',
    serialNumber: extracted.serialNumber || '',
    category: extracted.category || '',
    
    // Purchase Information  
    purchaseDate: extracted.purchaseDate || '',
    purchaseStore: extracted.retailer || '',
    purchaseAmount: undefined,
    
    // Warranty Information
    warrantyPeriod: extracted.warrantyPeriod || '',
    warrantyExpiryDate: warrantyExpiryDate,
    
    // AI Extraction Metadata
    confidence: 0.5, // Lower confidence for manual extraction
    extractionSource: 'OCR Text Pattern Matching',
    extractionTimestamp: new Date(),
    extractedText: ocrText,
    originalDocument: {
      fileName: 'document',
      size: 0,
      type: 'application/pdf'
    },
    
    // System Fields
    documentId: extractionResult.documentId,
    entryMethod: 'DOCUMENT_SCAN'
  };
};

export const SmartWarrantyAssistant: React.FC<SmartWarrantyAssistantProps> = ({
  className = "",
  onWarrantySaved,
  initialData
}) => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(initialData || null);
  const [processingStep, setProcessingStep] = useState('');
  
  // Modal and activity states
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  
  // Unified Progress Modal states
  const [showUnifiedProgress, setShowUnifiedProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepStatus, setStepStatus] = useState<StepStatus>({
    step1: 'pending',
    step2: 'pending',
    step3: 'pending'
  });
  const [stepData, setStepData] = useState<StepData>({
    uploadResult: null,
    extractedData: null,
    savedWarranty: null,
    error: null
  });
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<LocalSpeechRecognition | null>(null);

  // Add activity to history
  const addActivity = (type: Activity['type'], details: string, status: Activity['status'] = 'success') => {
    const newActivity: Activity = {
      id: Date.now().toString(),
      type,
      timestamp: new Date(),
      details,
      status
    };
    setActivities(prev => [newActivity, ...prev.slice(0, 9)]); // Keep last 10 activities
  };

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle manual entry - skip AI processing
  const handleManualEntry = () => {
    const emptyData: ExtractedData = {
      productName: '',
      brand: '',
      purchaseDate: '',
      purchaseStore: '',
      warrantyPeriod: '',
      confidence: 100
    };
    setExtractedData(emptyData);
    setShowExtractionModal(true);
    addActivity('warranty_added', 'Manual warranty entry started');
  };

  // Initialize speech recognition
  useEffect(() => {
    const windowWithSpeech = window as any;
    if ('SpeechRecognition' in windowWithSpeech || 'webkitSpeechRecognition' in windowWithSpeech) {
      const SpeechRecognition = windowWithSpeech.SpeechRecognition || windowWithSpeech.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition() as LocalSpeechRecognition;
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-IN';

        recognition.onresult = (event: LocalSpeechRecognitionEvent) => {
          const last = event.results.length - 1;
          const text = event.results[last][0].transcript;
          
          if (event.results[last].isFinal) {
            setIsListening(false);
            addActivity('voice_input', `Voice input: "${text.substring(0, 50)}..."`);
            setInput(prev => prev + ' ' + text);
          }
        };

        recognition.onerror = () => {
          setIsListening(false);
          addActivity('voice_input', 'Voice input failed', 'error');
        };

        recognitionRef.current = recognition;
      }
    }
  }, []);

  // Extract warranty data from text
  const extractFromText = (text: string): ExtractedData => {
    const data: ExtractedData = { confidence: 85 };

    // Product name extraction
    const productMatch = text.match(/(?:bought|purchased|got)\s+(?:a\s+)?([^,.]+?)(?:\s+(?:from|at|on))/i);
    if (productMatch) {
      data.productName = productMatch[1].trim();
    }

    // Brand extraction
    const brandMatch = text.match(/(?:samsung|apple|lg|sony|dell|hp|lenovo|asus|acer|microsoft|google|oneplus|xiaomi|huawei|oppo|vivo|realme|nokia|motorola|canon|nikon|bosch|whirlpool|godrej)/gi);
    if (brandMatch) {
      data.brand = brandMatch[0];
    }

    // Date extraction
    const dateMatches = text.match(/\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b|\b(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\b/g);
    if (dateMatches) {
      data.purchaseDate = dateMatches[0];
    }

    // Store extraction
    const storeMatch = text.match(/(?:from|at)\s+([^,.]+?)(?:\s+(?:on|in|for))/i);
    if (storeMatch) {
      data.purchaseStore = storeMatch[1].trim();
    }

    // Warranty period
    const warrantyMatches = text.match(/(\d+)\s*(year|month|yr|months)/gi);
    if (warrantyMatches) {
      data.warrantyPeriod = warrantyMatches[0];
    }

    return data;
  };

  // Process input with AI
  const processInput = async (inputText: string, type: 'text' | 'voice' = 'text') => {
    if (!inputText.trim()) return;

    setIsProcessing(true);
    setProcessingStep('Analyzing text...');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProcessingStep('Extracting warranty information...');
      
      // Extract data from text
      const extracted = extractFromText(inputText);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      setProcessingStep('Validating information...');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      setExtractedData(extracted);
      setShowExtractionModal(true);
      addActivity('ai_processed', `AI processed text and extracted warranty information`);
      setInput(''); // Clear input after processing
      setIsProcessing(false);
      setProcessingStep('');
      
    } catch (error) {
      console.error('Error processing input:', error);
      setIsProcessing(false);
      setProcessingStep('');
      addActivity('ai_processed', 'AI processing failed', 'error');
    }
  };

  // Handle voice input
  const startVoiceInput = () => {
    if (recognitionRef.current && !isListening) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      
      // Initialize unified progress modal
      setShowUnifiedProgress(true);
      setCurrentStep(1);
      setStepStatus({
        step1: 'processing',
        step2: 'pending',
        step3: 'pending'
      });
      setStepData({
        uploadResult: null,
        extractedData: null,
        savedWarranty: null,
        error: null
      });

      try {
        // Step 1: File Upload
        setCurrentStep(1);
        setStepStatus(prev => ({ ...prev, step1: 'processing' }));
        
        // Simulate upload
        await new Promise(resolve => setTimeout(resolve, 1500));
        const uploadResult: UploadResult = {
          fileId: Date.now().toString(),
          fileName: file.name,
          fileSize: file.size,
          uploadedAt: new Date(),
          url: URL.createObjectURL(file)
        };
        setStepData(prev => ({ ...prev, uploadResult }));
        setStepStatus(prev => ({ ...prev, step1: 'complete' }));
        
        // Step 2: Data Validation & Processing
        setTimeout(() => {
          setCurrentStep(2);
          setStepStatus(prev => ({ ...prev, step2: 'processing' }));
        }, 500);
        
      // Get current user for API call
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      console.log('Current user for API call:', currentUser);
      console.log('User ID being passed:', currentUser.userId);

      // Call real API for warranty extraction
      const extractionResult = await documentService.extractWarranty(file, currentUser.userId || '');
      
      console.log('📤 API Response received:', extractionResult);
      
      if (!extractionResult.success) {
        throw new Error(extractionResult.error || 'Failed to extract warranty information');
      }

      let extractedData: ExtractedData;

      // Check if we have structured warranty data or need LLM processing
      if (extractionResult.warrantyData) {
        // Case 1: We have structured warranty data from backend
        console.log('✅ Using structured warranty data from backend');
        const apiWarrantyData = extractionResult.warrantyData;
        extractedData = {
          // Core Product Information
          productName: apiWarrantyData.productName,
          brand: apiWarrantyData.brand,
          modelNumber: apiWarrantyData.modelNumber,
          serialNumber: apiWarrantyData.serialNumber,
          category: apiWarrantyData.category,
          
          // Purchase Information  
          purchaseDate: apiWarrantyData.purchaseDate,
          purchaseStore: apiWarrantyData.retailer,
          purchaseAmount: apiWarrantyData.purchaseAmount,
          
          // Warranty Information
          warrantyPeriod: apiWarrantyData.warrantyPeriod,
          warrantyExpiryDate: apiWarrantyData.warrantyExpiry,
          
          // AI Extraction Metadata
          confidence: extractionResult.confidence || apiWarrantyData.confidence || 0,
          extractionSource: 'Backend AI Processing',
          extractionTimestamp: new Date(),
          extractedText: extractionResult.ocrText || apiWarrantyData.extractedText,
          originalDocument: {
            fileName: file.name,
            size: file.size,
            type: file.type
          },
          
          // System Fields
          documentId: extractionResult.documentId,
          entryMethod: 'DOCUMENT_SCAN'
        };
      } else if (extractionResult.ocrText && !extractionResult.duplicate) {
        // Case 2: We have OCR text but no structured data - need LLM processing
        console.log('🔄 Processing OCR text with LLM for warranty extraction');
        
        try {
          // Call LLM processing endpoint
          const llmResult = await documentService.processOcrWithLLM(extractionResult.ocrText, extractionResult.documentId!);
          
          extractedData = {
            // Core Product Information
            productName: llmResult.productName,
            brand: llmResult.brand,
            modelNumber: llmResult.modelNumber,
            serialNumber: llmResult.serialNumber,
            category: llmResult.category,
            
            // Purchase Information  
            purchaseDate: llmResult.purchaseDate,
            purchaseStore: llmResult.retailer,
            purchaseAmount: llmResult.purchaseAmount,
            
            // Warranty Information
            warrantyPeriod: llmResult.warrantyPeriod,
            warrantyExpiryDate: llmResult.warrantyExpiry,
            
            // AI Extraction Metadata
            confidence: llmResult.confidence || extractionResult.confidence || 0,
            extractionSource: 'OCR + LLM Processing',
            extractionTimestamp: new Date(),
            extractedText: extractionResult.ocrText,
            originalDocument: {
              fileName: file.name,
              size: file.size,
              type: file.type
            },
            
            // System Fields
            documentId: extractionResult.documentId,
            entryMethod: 'DOCUMENT_SCAN'
          };
        } catch (llmError) {
          console.warn('⚠️ LLM processing failed, using manual extraction:', llmError);
          // Fallback to manual extraction from OCR text
          extractedData = extractWarrantyFromOcrText(extractionResult.ocrText!, extractionResult);
        }
      } else if (extractionResult.duplicate && extractionResult.duplicateInfo) {
        // Case 3: Duplicate document detected
        console.log('🔄 Duplicate document detected, handling existing data');
        
        if (extractionResult.duplicateInfo.existingWarranty) {
          // Use existing warranty data
          const existingWarranty = extractionResult.duplicateInfo.existingWarranty;
          extractedData = {
            // Core Product Information
            productName: existingWarranty.productName,
            brand: existingWarranty.brand,
            modelNumber: existingWarranty.modelNumber,
            serialNumber: existingWarranty.serialNumber,
            category: existingWarranty.category,
            
            // Purchase Information  
            purchaseDate: existingWarranty.purchaseDate,
            purchaseStore: existingWarranty.retailer,
            purchaseAmount: existingWarranty.purchaseAmount,
            
            // Warranty Information
            warrantyPeriod: existingWarranty.warrantyPeriod,
            warrantyExpiryDate: existingWarranty.warrantyExpiry,
            
            // AI Extraction Metadata
            confidence: extractionResult.duplicateInfo.existingConfidence || extractionResult.confidence || 0,
            extractionSource: 'Existing Document (Duplicate)',
            extractionTimestamp: new Date(),
            extractedText: existingWarranty.extractedText,
            originalDocument: {
              fileName: file.name,
              size: file.size,
              type: file.type
            },
            
            // System Fields
            documentId: extractionResult.documentId,
            entryMethod: 'DOCUMENT_SCAN'
          };
        } else {
          // No existing warranty, show message and empty form
          extractedData = {
            // Empty form for manual entry
            productName: '',
            brand: '',
            modelNumber: '',
            serialNumber: '',
            category: '',
            purchaseDate: '',
            purchaseStore: '',
            purchaseAmount: undefined,
            warrantyPeriod: '',
            warrantyExpiryDate: '',
            
            // AI Extraction Metadata
            confidence: 0,
            extractionSource: 'Duplicate Document - Manual Entry Required',
            extractionTimestamp: new Date(),
            extractedText: extractionResult.message,
            originalDocument: {
              fileName: file.name,
              size: file.size,
              type: file.type
            },
            
            // System Fields
            documentId: extractionResult.documentId,
            entryMethod: 'MANUAL'
          };
        }
      } else {
        throw new Error('No warranty data or OCR text received from backend');
      }
        
        setStepData(prev => ({ ...prev, extractedData }));
        setStepStatus(prev => ({ ...prev, step2: 'complete' }));
        
        // Step 3: Show Form for Review
        setTimeout(() => {
          setCurrentStep(3);
          setStepStatus(prev => ({ ...prev, step3: 'form' }));
        }, 500);
        
        addActivity('document_uploaded', `Document processed: ${file.name} (${Math.round(extractedData.confidence || 0)}% confidence)`);
        
        // Handle duplicate detection if API returns it
        if (extractionResult.duplicate && extractionResult.duplicateInfo) {
          addActivity('ai_processed', `Duplicate detected: Document uploaded on ${extractionResult.duplicateInfo.uploadedAt}`, 'pending');
        }
        
      } catch (error) {
        const currentStepNum = currentStep;
        setStepStatus(prev => ({ 
          ...prev, 
          [`step${currentStepNum}`]: 'error' 
        }));
        setStepData(prev => ({ ...prev, error: error instanceof Error ? error.message : String(error) }));
        addActivity('document_uploaded', `Failed to process: ${file.name}`, 'error');
      }
    }
  };

  // Save warranty
  const saveWarranty = async () => {
    if (!stepData.extractedData) return;

    try {
      // Get current user for API call
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Prepare warranty request data matching backend WarrantyRequest DTO
      const warrantyRequest = {
        productName: stepData.extractedData.productName!,
        purchaseDate: stepData.extractedData.purchaseDate!,
        purchaseStore: stepData.extractedData.purchaseStore!,
        warrantyExpiryDate: stepData.extractedData.warrantyExpiryDate!,
        category: stepData.extractedData.category || '',
        tags: stepData.extractedData.tags?.join(', ') || '',
        userId: currentUser.userId || '',
        documentId: stepData.extractedData.documentId || stepData.uploadResult?.fileId || undefined
      };
      
      console.log('Saving warranty with data:', warrantyRequest);
      
      // Call real warranty save API
      const savedWarranty = await saveWarrantyAPI(warrantyRequest);
      
      addActivity('warranty_added', `Warranty saved: ${stepData.extractedData.productName}`);
      
      if (onWarrantySaved) {
        onWarrantySaved(stepData.extractedData);
      }
      
      // Store saved warranty in step data
      setStepData(prev => ({ 
        ...prev, 
        savedWarranty
      }));
      
      // Reset other states
      setExtractedData(null);
      setShowExtractionModal(false);
      
    } catch (error) {
      console.error('Error saving warranty:', error);
      addActivity('warranty_added', 'Failed to save warranty', 'error');
      throw error; // Re-throw so the UI can handle the error state
    }
  };

  return (
    <div className={`smart-warranty-assistant ${className}`}>
      {/* Page Header */}
      <header className="warranty-ai-page-header">
        <div className="warranty-ai-page-header-content">
          <div>
            <h1 className="warranty-ai-page-title">
              <WarrantyIcon name="brain" className="warranty-ai-page-icon" />
              AI Warranty Assistant
            </h1>
            <p className="warranty-ai-page-subtitle">
              Intelligent warranty registration with <span className="warranty-ai-tamil-accent">සුවඩි</span> enterprise AI
            </p>
          </div>
          <div className="warranty-ai-page-actions">
            <button 
              className="btn btn-secondary btn-md"
              onClick={handleManualEntry}
              title="Skip AI and enter manually"
            >
              <WarrantyIcon name="file-plus" />
              Manual Entry
            </button>
            <button 
              className="btn btn-secondary btn-md"
              onClick={() => navigate('/warranties')}
            >
              ← Back to Warranties
            </button>
          </div>
        </div>
      </header>

      {/* Activity History Button */}
      <button 
        className="warranty-ai-activity-trigger"
        onClick={() => setShowActivityPanel(true)}
        title="Recent Activity"
        aria-label={`View recent activity (${activities.length} items)`}
      >
        <WarrantyIcon name="clock" />
        <span className="warranty-ai-activity-count">{activities.length}</span>
      </button>

      {/* Main Content */}
      <main className="warranty-ai-main-content">
        <div className="warranty-ai-content-container">
          
          {/* AI Input Section */}
          <section className="warranty-ai-section" aria-labelledby="ai-input-title">
            <div className="warranty-ai-container">
              <div className="warranty-ai-header">
                <h2 id="ai-input-title">Smart Warranty Processing</h2>
                <p>Describe your purchase naturally or upload documents - our AI will extract all warranty details</p>
              </div>

              <div className="warranty-ai-input-section">
                <div className="warranty-ai-input-wrapper">
                  <label htmlFor="warranty-input" className="sr-only">
                    Describe your warranty information
                  </label>
                  <textarea
                    id="warranty-input"
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Tell me about your purchase: 'I bought a Samsung Galaxy S24 from Best Buy on March 15th, 2024 with 2 year warranty...'"
                    className="warranty-ai-smart-input"
                    rows={4}
                    disabled={isProcessing}
                    aria-describedby="input-help"
                  />
                  <div id="input-help" className="sr-only">
                    Enter warranty details in natural language, use voice input, or upload documents
                  </div>
                  
                  <div className="warranty-ai-input-controls">
                    <div className="warranty-ai-input-options">
                      <button
                        className={`warranty-ai-option-btn warranty-ai-voice-btn ${isListening ? 'listening' : ''}`}
                        onClick={startVoiceInput}
                        disabled={isProcessing || isListening}
                        title="Voice Input"
                        aria-label={isListening ? 'Listening for voice input' : 'Start voice input'}
                      >
                        <WarrantyIcon name={isListening ? 'mic' : 'mic-off'} />
                        {isListening ? 'Listening...' : 'Voice'}
                      </button>
                      
                      <button
                        className="warranty-ai-option-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isProcessing}
                        title="Upload Receipt"
                        aria-label="Upload document or receipt"
                      >
                        <WarrantyIcon name="upload-cloud" />
                        Upload
                      </button>
                      
                      <button
                        className="warranty-ai-option-btn"
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={isProcessing}
                        title="Take Photo"
                        aria-label="Take photo of receipt"
                      >
                        <WarrantyIcon name="ocr-camera" />
                        Camera
                      </button>
                    </div>
                    
                    <button
                      className="warranty-ai-process-btn"
                      onClick={() => processInput(input, 'text')}
                      disabled={!input.trim() || isProcessing}
                      aria-label="Process text with AI"
                    >
                      {isProcessing ? (
                        <>
                          <WarrantyIcon name="brain" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <WarrantyIcon name="brain" />
                          Process with AI
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Processing Indicator */}
                {isProcessing && (
                  <div className="warranty-ai-processing-status" role="status" aria-live="polite">
                    <div className="warranty-ai-processing-spinner" aria-hidden="true"></div>
                    <span>{processingStep}</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Features Info */}
          <section className="warranty-ai-features-info" aria-labelledby="features-title">
            <h3 id="features-title">How AI Assistant Works</h3>
            <div className="warranty-ai-features-grid">
              <div className="warranty-ai-feature-item">
                <WarrantyIcon name="brain" className="warranty-ai-feature-icon" />
                <div>
                  <strong>Natural Language Processing</strong>
                  <p>Just describe your purchase naturally - our AI understands context</p>
                </div>
              </div>
              
              <div className="warranty-ai-feature-item">
                <WarrantyIcon name="mic" className="warranty-ai-feature-icon" />
                <div>
                  <strong>Voice Recognition</strong>
                  <p>Speak your warranty details in English, Hindi, or Tamil</p>
                </div>
              </div>
              
              <div className="warranty-ai-feature-item">
                <WarrantyIcon name="ocr-camera" className="warranty-ai-feature-icon" />
                <div>
                  <strong>Document Scanning</strong>
                  <p>Upload receipts or take photos for automatic text extraction</p>
                </div>
              </div>
              
              <div className="warranty-ai-feature-item">
                <WarrantyIcon name="warranty-shield" className="warranty-ai-feature-icon" />
                <div>
                  <strong>Instant Processing</strong>
                  <p>Get accurate warranty details in seconds with smart validation</p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Extraction Modal */}
      <ExtractionModal 
        isOpen={showExtractionModal}
        onClose={() => setShowExtractionModal(false)}
        extractedData={extractedData || {}}
        onSave={(data) => {
          setExtractedData(data);
          saveWarranty();
          setShowExtractionModal(false);
        }}
      />

      {/* Activity History Panel */}
      <ActivityHistoryPanel 
        isOpen={showActivityPanel}
        onClose={() => setShowActivityPanel(false)}
        activities={activities}
      />

      {/* Unified Progress Modal */}
      {showUnifiedProgress && (
        <div className="warranty-unified-progress-overlay">
          <div className="warranty-unified-progress-modal">
            <div className="warranty-unified-progress-header">
              <h3>Warranty Registration Progress</h3>
            </div>
            
            <div className="warranty-unified-progress-body">
              {/* Step 1: File Upload */}
              <div className={`warranty-progress-step ${currentStep >= 1 ? 'active' : ''} ${stepStatus.step1}`}>
                <div className="warranty-progress-step-number">1</div>
                <div className="warranty-progress-step-content">
                  <h4>File Upload</h4>
                  <p>
                    {stepStatus.step1 === 'processing' && 'Uploading your document...'}
                    {stepStatus.step1 === 'complete' && 'Document uploaded successfully!'}
                    {stepStatus.step1 === 'error' && `Upload failed: ${stepData.error}`}
                    {stepStatus.step1 === 'pending' && 'Waiting to upload document'}
                  </p>
                </div>
                <div className="warranty-progress-step-status">
                  {stepStatus.step1 === 'processing' && <div className="spinner"></div>}
                  {stepStatus.step1 === 'complete' && <span className="checkmark">✓</span>}
                  {stepStatus.step1 === 'error' && <span className="error-mark">✗</span>}
                </div>
              </div>

              {/* Step 2: Data Validation */}
              <div className={`warranty-progress-step ${currentStep >= 2 ? 'active' : ''} ${stepStatus.step2}`}>
                <div className="warranty-progress-step-number">2</div>
                <div className="warranty-progress-step-content">
                  <h4>Data Validation & Processing</h4>
                  <p>
                    {stepStatus.step2 === 'processing' && 'Extracting warranty information...'}
                    {stepStatus.step2 === 'complete' && stepData.extractedData && (
                      <div className="extraction-results">
                        <div className="extraction-summary">
                          <div className="confidence-indicator">
                            <span className="confidence-label">Overall Confidence:</span>
                            <div className="confidence-bar">
                              <div 
                                className="confidence-fill" 
                                style={{ width: `${stepData.extractedData.confidence}%` }}
                              ></div>
                            </div>
                            <span className="confidence-value">{stepData.extractedData.confidence}%</span>
                          </div>
                          <div className="document-info">
                            <p><strong>Document:</strong> {stepData.extractedData.originalDocument?.fileName}</p>
                            <p><strong>Extraction Source:</strong> {stepData.extractedData.extractionSource}</p>
                            <p><strong>Extracted At:</strong> {stepData.extractedData.extractionTimestamp?.toLocaleString()}</p>
                          </div>
                        </div>
                        
                        <div className="extracted-fields">
                          <h5>Extracted Information:</h5>
                          <div className="field-grid">
                            {stepData.extractedData.productName && (
                              <div className="field-item">
                                <span className="field-label">Product:</span>
                                <span className="field-value">{stepData.extractedData.productName}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.productName}%</span>
                              </div>
                            )}
                            {stepData.extractedData.brand && (
                              <div className="field-item">
                                <span className="field-label">Brand:</span>
                                <span className="field-value">{stepData.extractedData.brand}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.brand}%</span>
                              </div>
                            )}
                            {stepData.extractedData.modelNumber && (
                              <div className="field-item">
                                <span className="field-label">Model:</span>
                                <span className="field-value">{stepData.extractedData.modelNumber}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.modelNumber}%</span>
                              </div>
                            )}
                            {stepData.extractedData.purchaseDate && (
                              <div className="field-item">
                                <span className="field-label">Purchase Date:</span>
                                <span className="field-value">{stepData.extractedData.purchaseDate}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.purchaseDate}%</span>
                              </div>
                            )}
                            {stepData.extractedData.purchaseStore && (
                              <div className="field-item">
                                <span className="field-label">Store:</span>
                                <span className="field-value">{stepData.extractedData.purchaseStore}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.purchaseStore}%</span>
                              </div>
                            )}
                            {stepData.extractedData.serialNumber && (
                              <div className="field-item">
                                <span className="field-label">Serial:</span>
                                <span className="field-value">{stepData.extractedData.serialNumber}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.serialNumber}%</span>
                              </div>
                            )}
                            {stepData.extractedData.purchaseAmount && (
                              <div className="field-item">
                                <span className="field-label">Amount:</span>
                                <span className="field-value">${stepData.extractedData.purchaseAmount}</span>
                                <span className="field-confidence">{stepData.extractedData.fieldConfidence?.purchaseAmount}%</span>
                              </div>
                            )}
                          </div>
                          <div className="step2-actions">
                            <button 
                              onClick={() => {
                                setCurrentStep(3);
                                setStepStatus(prev => ({ ...prev, step3: 'form' }));
                              }}
                              className="btn-primary review-button"
                            >
                              Review & Edit Details →
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {stepStatus.step2 === 'error' && `Processing failed: ${stepData.error}`}
                    {stepStatus.step2 === 'pending' && 'Waiting for file upload'}
                  </p>
                </div>
                <div className="warranty-progress-step-status">
                  {stepStatus.step2 === 'processing' && <div className="spinner"></div>}
                  {stepStatus.step2 === 'complete' && <span className="checkmark">✓</span>}
                  {stepStatus.step2 === 'error' && <span className="error-mark">✗</span>}
                </div>
              </div>

              {/* Step 3: Final Results / Form */}
              <div className={`warranty-progress-step ${currentStep >= 3 ? 'active' : ''} ${stepStatus.step3}`}>
                <div className="warranty-progress-step-number">3</div>
                <div className="warranty-progress-step-content">
                  <h4>Review & Save Warranty</h4>
                  {stepStatus.step3 === 'form' && stepData.extractedData && (
                    <div className="warranty-form-inline">
                      <p>Please review and confirm all warranty details before saving:</p>
                      <div className="warranty-form-comprehensive">
                        
                        {/* Product Information */}
                        <div className="form-section">
                          <h5>Product Information</h5>
                          <div className="warranty-form-grid">
                            <div className="form-field">
                              <label>Product Name *</label>
                              <input 
                                type="text" 
                                placeholder="Enter product name"
                                value={stepData.extractedData.productName || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, productName: e.target.value }
                                }))}
                                required
                              />
                            </div>
                            <div className="form-field">
                              <label>Brand</label>
                              <input 
                                type="text" 
                                placeholder="Enter brand name"
                                value={stepData.extractedData.brand || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, brand: e.target.value }
                                }))}
                              />
                            </div>
                            <div className="form-field">
                              <label>Model Number</label>
                              <input 
                                type="text" 
                                placeholder="Enter model number"
                                value={stepData.extractedData.modelNumber || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, modelNumber: e.target.value }
                                }))}
                              />
                            </div>
                            <div className="form-field">
                              <label>Serial Number</label>
                              <input 
                                type="text" 
                                placeholder="Enter serial number"
                                value={stepData.extractedData.serialNumber || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, serialNumber: e.target.value }
                                }))}
                              />
                            </div>
                            <div className="form-field">
                              <label>Category</label>
                              <select 
                                value={stepData.extractedData.category || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, category: e.target.value }
                                }))}
                              >
                                <option value="">Select category</option>
                                <option value="electronics">Electronics</option>
                                <option value="appliances">Appliances</option>
                                <option value="automotive">Automotive</option>
                                <option value="home">Home & Garden</option>
                                <option value="tools">Tools & Equipment</option>
                                <option value="furniture">Furniture</option>
                                <option value="jewelry">Jewelry & Watches</option>
                                <option value="sports">Sports & Recreation</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Purchase Information */}
                        <div className="form-section">
                          <h5>Purchase Information</h5>
                          <div className="warranty-form-grid">
                            <div className="form-field">
                              <label>Purchase Date *</label>
                              <input 
                                type="date" 
                                value={stepData.extractedData.purchaseDate || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, purchaseDate: e.target.value }
                                }))}
                                required
                              />
                            </div>
                            <div className="form-field">
                              <label>Purchase Store *</label>
                              <input 
                                type="text" 
                                placeholder="Where did you buy this?"
                                value={stepData.extractedData.purchaseStore || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, purchaseStore: e.target.value }
                                }))}
                                required
                              />
                            </div>
                            <div className="form-field">
                              <label>Purchase Amount</label>
                              <input 
                                type="number" 
                                step="0.01"
                                placeholder="Enter purchase amount"
                                value={stepData.extractedData.purchaseAmount || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, purchaseAmount: parseFloat(e.target.value) || undefined }
                                }))}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Warranty Information */}
                        <div className="form-section">
                          <h5>Warranty Information</h5>
                          <div className="warranty-form-grid">
                            <div className="form-field">
                              <label>Warranty Period</label>
                              <input 
                                type="text" 
                                placeholder="e.g., 2 years, 12 months"
                                value={stepData.extractedData.warrantyPeriod || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, warrantyPeriod: e.target.value }
                                }))}
                              />
                            </div>
                            <div className="form-field">
                              <label>Warranty Expiry Date *</label>
                              <input 
                                type="date" 
                                value={stepData.extractedData.warrantyExpiryDate || ''}
                                onChange={(e) => setStepData(prev => ({
                                  ...prev,
                                  extractedData: { ...prev.extractedData!, warrantyExpiryDate: e.target.value }
                                }))}
                                required
                              />
                            </div>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="form-section">
                          <h5>Tags (Optional)</h5>
                          <div className="form-field">
                            <label>Add tags to help organize your warranties</label>
                            <input 
                              type="text" 
                              placeholder="e.g., smartphone, flagship, premium (comma separated)"
                              value={stepData.extractedData.tags?.join(', ') || ''}
                              onChange={(e) => setStepData(prev => ({
                                ...prev,
                                extractedData: { 
                                  ...prev.extractedData!, 
                                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                                }
                              }))}
                            />
                          </div>
                        </div>

                      </div>
                      <div className="warranty-form-actions">
                        <button 
                          onClick={() => setShowUnifiedProgress(false)}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={() => {
                            setCurrentStep(2);
                            setStepStatus(prev => ({ ...prev, step3: 'pending' }));
                          }}
                          className="btn-outline"
                        >
                          ← Back to Review
                        </button>
                        <button 
                          onClick={async () => {
                            setStepStatus(prev => ({ ...prev, step3: 'processing' }));
                            try {
                              await saveWarranty();
                              setStepStatus(prev => ({ ...prev, step3: 'complete' }));
                              setTimeout(() => setShowUnifiedProgress(false), 2000);
                            } catch (error) {
                              setStepStatus(prev => ({ ...prev, step3: 'error' }));
                              setStepData(prev => ({ ...prev, error: error instanceof Error ? error.message : String(error) }));
                            }
                          }}
                          className="btn-primary"
                          disabled={!stepData.extractedData.productName || !stepData.extractedData.purchaseDate || !stepData.extractedData.purchaseStore || !stepData.extractedData.warrantyExpiryDate}
                        >
                          Save Warranty
                        </button>
                      </div>
                    </div>
                  )}
                  {stepStatus.step3 === 'processing' && <p>Saving warranty...</p>}
                  {stepStatus.step3 === 'complete' && <p>✅ Warranty saved successfully!</p>}
                  {stepStatus.step3 === 'error' && <p>❌ Error saving warranty: {stepData.error}</p>}
                  {stepStatus.step3 === 'pending' && <p>Ready for review and editing</p>}
                </div>
                <div className="warranty-progress-step-status">
                  {stepStatus.step3 === 'processing' && <div className="spinner"></div>}
                  {stepStatus.step3 === 'complete' && <span className="checkmark">✓</span>}
                  {stepStatus.step3 === 'error' && <span className="error-mark">✗</span>}
                  {stepStatus.step3 === 'form' && <span className="form-icon">📝</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        onChange={(e) => handleFileUpload(e.target.files)}
        style={{ display: 'none' }}
        aria-label="File upload input"
      />
      
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileUpload(e.target.files)}
        style={{ display: 'none' }}
        aria-label="Camera capture input"
      />
    </div>
  );
};

export default SmartWarrantyAssistant;
